/**
 * 政府端 — 告警台账（级别/类型/时间/处理）
 */
import { useEffect, useState } from 'react';
import { fetchAlerts, escalateAlert, resolveAlert } from '@/api/transit';
import { AlertLevelTag, Empty, Loading, PageHeader, StatusTag } from '@/components/common';
import type { TransitAlert } from '@/types';
import { alertHandleText, alertTypeText, fmtTime } from '@/utils/format';

const typeFilter = [
  { label: '全部', value: '' },
  { label: '越界', value: 1 },
  { label: '偏航', value: 2 },
  { label: '超时', value: 3 },
  { label: '温湿度', value: 4 },
  { label: '证照', value: 5 },
  { label: '数据中断', value: 6 },
];

export default function GovAlerts() {
  const [alerts, setAlerts] = useState<TransitAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<number | ''>('');

  const load = (t: number | '') => {
    setLoading(true);
    fetchAlerts(t === '' ? {} : { alert_type: t }).then((d) => {
      setAlerts(d.list);
      setLoading(false);
    });
  };

  useEffect(() => {
    load(type);
  }, [type]);

  const handleEscalate = async (a: TransitAlert) => {
    await escalateAlert(a.id);
    load(type);
  };

  const handleResolve = async (a: TransitAlert) => {
    await resolveAlert(a.id);
    load(type);
  };

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader title="告警台账" sub="全部运输单告警，支持分级与类型筛选" />
      <div className="card">
        <div className="row" style={{ marginBottom: 14 }}>
          {typeFilter.map((f) => (
            <button
              key={String(f.value)}
              className={`btn btn-sm ${type === f.value ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setType(f.value as number | '')}
            >
              {f.label}
            </button>
          ))}
        </div>
        {alerts.length === 0 ? (
          <Empty text="暂无告警" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>运输单</th>
                <th>类型</th>
                <th>级别</th>
                <th>内容</th>
                <th>时间</th>
                <th>处理状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>#{a.transit_id}</td>
                  <td>{alertTypeText(a.alert_type)}</td>
                  <td>
                    <AlertLevelTag level={a.alert_level} />
                  </td>
                  <td style={{ maxWidth: 380 }}>{a.alert_content}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-ink-secondary)' }}>{fmtTime(a.created_at)}</td>
                  <td>
                    <StatusTag tone={a.handle_status === 2 ? 'green' : a.handle_status === 3 ? 'red' : a.handle_status === 1 ? 'amber' : 'muted'}>
                      {alertHandleText(a.handle_status)}
                    </StatusTag>
                  </td>
                  <td>
                    {a.handle_status !== 2 && (
                      <div className="row" style={{ gap: 6 }}>
                        <button className="btn btn-sm btn-amber" onClick={() => handleEscalate(a)}>
                          升级
                        </button>
                        <button className="btn btn-sm btn-green" onClick={() => handleResolve(a)}>
                          解除
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
